import { LanguageLocalDatasource } from "@core/data/datasources/language-local.datasource.ts";
import { LanguageRemoteDatasource } from "@core/data/datasources/language-remote.datasource.ts";
import { FallbackError } from "@core/domain/errors/fallback.error.ts";
import { ValidationError } from "@core/domain/errors/validation.error.ts";
import type { Language } from "@core/domain/models/language.model.ts";
import {
    LanguageIsoCodeEnum,
    toLanguageIsoCode,
} from "@core/domain/models/language.model.ts";
import type { LanguageRepository } from "@core/domain/repositories/language.repository.ts";
import type { SelectOption } from "@core/domain/types/select-option";
import type { Undefinable } from "@core/domain/types/undefinable.type.ts";
import { inject, injectable } from "inversify";
import type { Result, ResultAsync } from "neverthrow";
import { ok, okAsync } from "neverthrow";
import { BehaviorSubject, Observable } from "rxjs";

@injectable()
export class LanguageImplRepository implements LanguageRepository {
    private languages: Undefinable<Language[]>;

    private _currentLanguage: BehaviorSubject<Undefinable<string>> =
        new BehaviorSubject<Undefinable<string>>(undefined);

    get currentLanguage(): Observable<Undefinable<string>> {
        return this._currentLanguage;
    }

    constructor(
        @inject(LanguageRemoteDatasource)
        private readonly languageRemoteDatasource: LanguageRemoteDatasource,
        @inject(LanguageLocalDatasource)
        private readonly languageLocalDatasource: LanguageLocalDatasource,
    ) {}

    getAll(): ResultAsync<Language[], FallbackError> {
        if (this.languages) {
            return okAsync(this.languages);
        }

        return this.languageRemoteDatasource.fetchAll().map((languages) => {
            this.languages = languages;
            return languages;
        });
    }

    getCurrent(): Result<string, FallbackError> {
        if (this._currentLanguage.value) {
            return ok(this._currentLanguage.value);
        }

        return this.languageLocalDatasource
            .getCurrent()
            .map((currentLanguage) => {
                this._currentLanguage.next(currentLanguage);
                return currentLanguage;
            });
    }

    getDefault(): LanguageIsoCodeEnum {
        return this.languageLocalDatasource
            .getDefault()
            .map((defaultLanguage) =>
                toLanguageIsoCode(defaultLanguage, LanguageIsoCodeEnum.English),
            )
            .unwrapOr(LanguageIsoCodeEnum.English);
    }

    update(
        language: LanguageIsoCodeEnum,
    ): ResultAsync<true, ValidationError | FallbackError> {
        return this.languageRemoteDatasource
            .update(language)
            .andThen(() => this.languageLocalDatasource.update(language))
            .map(() => {
                this._currentLanguage.next(language);
                return true;
            });
    }

    languageAll(): SelectOption<LanguageIsoCodeEnum>[] {
        return this.languageLocalDatasource.getAll();
    }
}
